import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import { Invoice, Order } from "@/api.types";
import * as z from "zod";

// Types
interface InvoicesResponse {
  data: Invoice[];
  metadata: {
    total: number;
    page: number;
    totalPages: number;
  };
}

// Schema
const createInvoiceSchema = z.object({
  customerId: z.string().nonempty("Customer is required"),
  orderIds: z
    .array(z.string())
    .min(1, "At least one order must be selected")
    .max(
      500,
      "Cannot create invoice with more than 500 orders. Please select fewer orders or split into multiple invoices."
    ),
  dueDate: z.date(),
  discount: z
    .object({
      type: z.enum(["percentage", "amount"]),
      value: z.number().min(0).max(100000),
    })
    .optional(),
});

export type CreateInvoiceSchema = z.infer<typeof createInvoiceSchema>;

// Get invoices with pagination
export function useInvoices(page: number, limit: number) {
  const { token } = useSession();

  return useQuery<InvoicesResponse>({
    queryKey: ["invoices", page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      const { data } = await axios.get<InvoicesResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/invoices?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

// Get customer orders for invoice creation
export function useCustomerOrders(
  customerId: string | undefined,
  dateRange: { from: Date; to: Date } | undefined,
  page = 1,
  limit = 100
) {
  const { token } = useSession();

  return useQuery<{
    data: Order[];
    metadata: { totalCount: number; totalPages: number };
  }>({
    queryKey: [
      "customerOrders",
      customerId,
      dateRange?.from,
      dateRange?.to,
      page,
      limit,
    ],
    queryFn: async () => {
      if (!dateRange?.from || !dateRange?.to)
        return { data: [], metadata: { totalCount: 0, totalPages: 0 } };

      const fromDate = new Date(dateRange.from);
      fromDate.setHours(0, 0, 0, 0);

      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59, 999);

      const { data } = await axios.get<{
        data: Order[];
        metadata: { totalCount: number; totalPages: number };
      }>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customerId}/orders`,
        {
          params: {
            from: fromDate.toISOString(),
            to: toDate.toISOString(),
            invoiceable: true,
            type: "all",
            page,
            limit,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token && !!customerId && !!dateRange?.from && !!dateRange?.to,
  });
}

// Create invoice mutation
export function useCreateInvoice() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<Invoice, Error, CreateInvoiceSchema>({
    mutationFn: async (values) => {
      const { data } = await axios.post<Invoice>(
        `${import.meta.env.VITE_API_URL}/api/v1/invoices`,
        values,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
}
